// Iter93 — "Book coaches near me" lightweight map view.
// -----------------------------------------------------------------------------
// Uses Leaflet + OpenStreetMap tiles (no API key, no CORS drama). Loads regions
// via `/regions/nearby?postcode=…` and drops a marker for every region within
// the selected radius. Each marker popup gives a one-tap "See sessions" CTA
// that filters /offerings by that region (via localStorage `rl_region_filter`).
//
// Deliberately minimal — no clustering, no coach avatars pinned individually.
// A region marker with a coach + offering count is more than enough for MVP
// and keeps the bundle size low.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Users, CalendarDays, Zap, ArrowLeft, Home, X } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { buildAvatarUrl } from "@/components/ProfileImageUpload";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";

// Leaflet's default marker icons ship as separate PNG files bundled by
// webpack in an unusual way; explicitly point at CDN URLs so we don't need
// to configure asset paths. Green icon for regions with sessions, grey when
// there are none yet.
const iconFor = (hasSessions) => new L.Icon({
  iconUrl: hasSessions
    ? "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png"
    : "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const meIcon = new L.DivIcon({
  html: '<div style="width:16px;height:16px;background:#CCFF00;border:3px solid #0A0A0A;border-radius:50%;box-shadow:0 0 0 2px #CCFF00,0 0 12px rgba(204,255,0,0.8);"></div>',
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Auto-fit the map to whatever bounds we've been given, animated so the
// user visibly sees the map re-frame when they change radius.
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    try {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11, animate: true });
    } catch (_e) { /* silent */ }
  }, [bounds, map]);
  return null;
}

export default function CoachesNearMe() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const postcode = ((user?.personal_details || {}).postcode || "").trim();
  const [radius, setRadius] = useState(50);
  // Iter103 — "Show All Regions" override. Default off = keeps the regional
  // filter (results within `radius` of the student's postcode). Toggling on
  // fetches every active region so students can browse beyond their home.
  // Persisted in localStorage so the choice sticks between visits.
  const [showAllRegions, setShowAllRegions] = useState(() => {
    try { return window.localStorage.getItem("rl_show_all_regions") === "1"; }
    catch (_e) { return false; }
  });
  const toggleShowAll = (next) => {
    setShowAllRegions(next);
    try { window.localStorage.setItem("rl_show_all_regions", next ? "1" : "0"); } catch (_e) { /* private mode */ }
  };
  const [state, setState] = useState({ loading: true, origin: null, results: [], error: "" });

  // Iter93c — "Save My Home" one-shot prompt. Fires when:
  //   · caller is a student
  //   · user.home_region_id is NOT yet set
  //   · nearby-regions returned ≥1 result
  //   · localStorage flag rl_home_prompt_shown is not set (we only ask once)
  // The user can either accept (PUT /me/home-region) or dismiss (X). Either
  // way the flag gets set so we don't nag them on every visit.
  const [homePrompt, setHomePrompt] = useState({ open: false, region: null });

  useEffect(() => {
    if (!postcode && !showAllRegions) { setState({ loading: false, origin: null, results: [], error: "" }); return; }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    (async () => {
      try {
        // Iter103 — Show-All uses the biggest radius (200mi cap) so nearly
        // every active region comes back. Keeps the same endpoint so the
        // rest of the UI (distance chips, coach previews) still works.
        const effectiveRadius = showAllRegions ? 200 : radius;
        const effectivePostcode = postcode || "SW1A1AA";  // London fallback for anon browsing when Show-All is on
        const { data } = await api.get("/regions/nearby", { params: { postcode: effectivePostcode, radius_miles: effectiveRadius } });
        if (cancelled) return;
        const results = data?.results || [];
        setState({ loading: false, origin: data?.origin || null, results, error: "" });
        // Iter93c — trigger the one-shot home-region prompt if this is the
        // student's first useful visit. Uses localStorage as the "asked once"
        // marker, keyed per user so multiple accounts on one device each get
        // one shot at it. Skip when Show-All is on — the list isn't relevant
        // to their home region anymore.
        if (showAllRegions) return;
        const promptKey = `rl_home_prompt_shown_${user?.id || "anon"}`;
        try {
          const asked = window.localStorage.getItem(promptKey);
          if (!asked && !user?.home_region_id && user?.role === "student" && results.length > 0) {
            setHomePrompt({ open: true, region: results[0] });
          }
        } catch (_e) { /* private-mode / SSR safety */ }
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, origin: null, results: [], error: formatApiError(e.response?.data?.detail) || "Couldn't load map" });
      }
    })();
    return () => { cancelled = true; };
  }, [postcode, radius, showAllRegions, user?.id, user?.home_region_id, user?.role]);

  const dismissHomePrompt = () => {
    try { window.localStorage.setItem(`rl_home_prompt_shown_${user?.id || "anon"}`, "1"); } catch (_e) { /* silent */ }
    setHomePrompt({ open: false, region: null });
  };

  const acceptHomePrompt = async () => {
    const r = homePrompt.region;
    if (!r) return dismissHomePrompt();
    try {
      await api.put("/me/home-region", { region_id: r.id });
      try { window.localStorage.setItem("rl_region_filter", r.id); } catch (_e) { /* silent */ }
      try {
        const me = await api.get("/auth/me");
        setUser && setUser(me.data);
      } catch (_e) { /* refresh best-effort */ }
      toast.success(`Home region set — ${r.label}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set home region");
    } finally {
      dismissHomePrompt();
    }
  };

  // Compute bounds for auto-fit — origin + all region markers so nothing
  // ends up off-screen.
  const bounds = useMemo(() => {
    const pts = [];
    if (state.origin) pts.push([state.origin.lat, state.origin.lng]);
    state.results.forEach((r) => {
      if (typeof r.centre_lat === "number" && typeof r.centre_lng === "number") {
        pts.push([r.centre_lat, r.centre_lng]);
      }
    });
    return pts;
  }, [state.origin, state.results]);

  // If the student hasn't set a postcode yet, prompt them — the map is
  // useless without an origin.
  if (!postcode) {
    return (
      <div className="min-h-screen bg-black text-white px-4 sm:px-8 py-8">
        <div className="max-w-3xl mx-auto space-y-6" data-testid="coaches-near-me-no-postcode">
          <BackLink />
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Coaches near you</h1>
          <div className="card-surface p-6 space-y-3">
            <div className="label-mono text-[#CCFF00]">POSTCODE REQUIRED</div>
            <p className="text-sm text-[#A3A3A3]">
              Add a postcode to your personal details and we&apos;ll drop pins on every Rally Lab region within reach — plus how many coaches and upcoming sessions live there.
            </p>
            <Link
              to="/profile/personal"
              data-testid="add-postcode-cta"
              className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-4 py-2 font-semibold text-sm hover:brightness-95"
            >
              <MapPin className="w-4 h-4" /> Add postcode
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const totalCoaches = state.results.reduce((n, r) => n + (r.coach_count || 0), 0);
  const totalSessions = state.results.reduce((n, r) => n + (r.active_offering_count || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="coaches-near-me-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 space-y-4">
        <BackLink />
        {/* Iter93c — Save My Home prompt (one-shot per student, dismissible).
            Rendered right at the top so it doesn't fight for attention with
            the map. Auto-hides once the student either accepts or dismisses. */}
        {homePrompt.open && homePrompt.region && (
          <div className="card-surface p-4 flex items-center gap-3 border-l-4" style={{ borderColor: "#CCFF00" }} data-testid="home-region-prompt">
            <Home className="w-5 h-5 text-[#CCFF00] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">
                <span className="font-semibold">{homePrompt.region.label}</span> is your closest region — set it as your home so bookings default to sessions near you?
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                {homePrompt.region.distance_miles} mi away · {homePrompt.region.active_offering_count || 0} upcoming session{(homePrompt.region.active_offering_count || 0) === 1 ? "" : "s"}
              </div>
            </div>
            <button
              type="button"
              onClick={acceptHomePrompt}
              data-testid="home-region-accept"
              className="btn-volt bg-[#CCFF00] text-black px-4 py-2 text-xs uppercase tracking-wider font-semibold hover:brightness-95"
            >
              Set as home
            </button>
            <button
              type="button"
              onClick={dismissHomePrompt}
              data-testid="home-region-dismiss"
              className="p-1 text-[#A3A3A3] hover:text-white"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Coaches near you</h1>
            <p className="text-sm text-[#A3A3A3] mt-1">
              Pinned on the map from your postcode <span className="text-white font-mono">{postcode.toUpperCase()}</span>.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="radius" className="label-mono text-[#A3A3A3]">Radius</label>
            <select
              id="radius"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              disabled={showAllRegions}
              data-testid="radius-picker"
              className="bg-[#0A0A0A] border border-[#262626] px-3 py-1.5 text-sm focus:border-[#CCFF00] focus:outline-none disabled:opacity-40"
            >
              <option value={10}>10 miles</option>
              <option value={25}>25 miles</option>
              <option value={50}>50 miles</option>
              <option value={100}>100 miles</option>
            </select>
            {/* Iter103 — Show-All override. Ignores radius/postcode and
                surfaces every active region so students can browse coaches
                beyond their home area. Sticks via localStorage. */}
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="show-all-regions-label">
              <input
                type="checkbox"
                checked={showAllRegions}
                onChange={(e) => toggleShowAll(e.target.checked)}
                data-testid="show-all-regions-toggle"
                className="w-4 h-4 accent-[#CCFF00]"
              />
              <span className={showAllRegions ? "text-[#CCFF00] font-semibold" : "text-[#A3A3A3]"}>Show all regions</span>
            </label>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard icon={<MapPin className="w-4 h-4" />} label="Regions" value={state.results.length} testid="stat-regions" />
          <StatCard icon={<Users className="w-4 h-4" />} label="Coaches" value={totalCoaches} testid="stat-coaches" />
          <StatCard icon={<CalendarDays className="w-4 h-4" />} label="Upcoming sessions" value={totalSessions} testid="stat-sessions" />
        </div>

        {/* Map surface */}
        <div className="card-surface overflow-hidden" style={{ height: "560px" }} data-testid="map-surface">
          {state.loading && (
            <div className="h-full flex items-center justify-center text-sm text-[#A3A3A3]" data-testid="map-loading">Plotting nearby regions…</div>
          )}
          {!state.loading && state.error && (
            <div className="h-full flex items-center justify-center text-sm text-[#FF4500]" data-testid="map-error">{state.error}</div>
          )}
          {!state.loading && !state.error && !state.origin && (
            <div className="h-full flex items-center justify-center text-sm text-[#A3A3A3]" data-testid="map-no-origin">
              Couldn&apos;t place that postcode on the map. Double-check it in your personal details.
            </div>
          )}
          {!state.loading && !state.error && state.origin && (
            <MapContainer
              center={[state.origin.lat, state.origin.lng]}
              zoom={8}
              scrollWheelZoom
              style={{ height: "100%", width: "100%", background: "#0A0A0A" }}
              attributionControl
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* The reach radius as a translucent volt-yellow disc so students
                  instantly grok what "50 miles" looks like on the ground. */}
              <Circle
                center={[state.origin.lat, state.origin.lng]}
                radius={radius * 1609.34}
                pathOptions={{ color: "#CCFF00", weight: 1, opacity: 0.6, fillColor: "#CCFF00", fillOpacity: 0.06 }}
              />
              <Marker position={[state.origin.lat, state.origin.lng]} icon={meIcon}>
                <Popup><span className="text-black">You&apos;re here — {postcode.toUpperCase()}</span></Popup>
              </Marker>
              {state.results.map((r) => (
                <Marker
                  key={r.id}
                  position={[r.centre_lat, r.centre_lng]}
                  icon={iconFor((r.active_offering_count || 0) > 0)}
                >
                  <Popup>
                    <div style={{ minWidth: 220 }} data-testid={`map-popup-${r.slug}`}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#000" }}>{r.label}</div>
                      <div style={{ fontSize: 11, color: "#525252", marginTop: 2 }}>
                        {r.distance_miles} miles away{r.parent_region ? ` · ${r.parent_region}` : ""}
                      </div>
                      {/* Iter93c — coach faces. Shows up to 4 approved avatars
                          + a "+N more" pill when the region has more coaches
                          than we render. Skips silently if none of the coaches
                          have approved headshots. */}
                      {(r.coaches || []).length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", marginTop: 8, gap: 4 }} data-testid={`map-coaches-${r.slug}`}>
                          {(r.coaches || []).slice(0, 4).map((c) => {
                            const url = buildAvatarUrl(c.avatar_path);
                            return (
                              <div
                                key={c.id}
                                title={c.is_away ? `${c.name} · Away${c.away_note ? ` — ${c.away_note}` : ""}` : c.name}
                                style={{
                                  width: 28, height: 28, borderRadius: "50%", overflow: "hidden",
                                  background: "#0A0A0A", color: "#CCFF00", fontWeight: 700, fontSize: 10,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  border: c.is_away ? "1.5px solid #FF9F1C" : "1.5px solid #FFFFFF",
                                  opacity: c.is_away ? 0.55 : 1,
                                  position: "relative",
                                }}
                              >
                                {url ? (
                                  <img src={url} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt={c.name || "Coach"} />
                                ) : (
                                  <span>{(c.name || "?").charAt(0).toUpperCase()}</span>
                                )}
                              </div>
                            );
                          })}
                          {(r.coach_count || 0) > 4 && (
                            <span style={{ fontSize: 10, color: "#525252", marginLeft: 4 }}>+{(r.coach_count || 0) - 4}</span>
                          )}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: "#000", marginTop: 8, lineHeight: 1.6 }}>
                        <div>{r.coach_count || 0} coach{(r.coach_count || 0) === 1 ? "" : "es"}</div>
                        <div>{r.active_offering_count || 0} upcoming session{(r.active_offering_count || 0) === 1 ? "" : "s"}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          try { localStorage.setItem("rl_region_filter", r.id); } catch (_e) { /* private mode */ }
                          nav(`/offerings`);
                        }}
                        data-testid={`map-book-${r.slug}`}
                        style={{
                          marginTop: 10,
                          padding: "6px 10px",
                          background: "#CCFF00",
                          color: "#000",
                          border: 0,
                          fontWeight: 700,
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        See sessions →
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
              <FitBounds bounds={bounds} />
            </MapContainer>
          )}
        </div>

        {/* Region list underneath the map — same data, just tabular for
            keyboard/screen-reader friendliness and mobile fallback. */}
        {!state.loading && state.results.length > 0 && (
          <div className="space-y-2" data-testid="nearby-region-list">
            <div className="label-mono text-[#A3A3A3]">SORTED BY DISTANCE</div>
            {state.results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  try { localStorage.setItem("rl_region_filter", r.id); } catch (_e) { /* silent */ }
                  nav(`/offerings`);
                }}
                data-testid={`nearby-list-${r.slug}`}
                className="w-full text-left border border-[#262626] hover:border-[#CCFF00] p-3 flex items-center gap-3 transition-colors"
              >
                <MapPin className="w-4 h-4 text-[#CCFF00] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                    {r.distance_miles} mi · {r.coach_count || 0} coaches · {r.active_offering_count || 0} sessions
                  </div>
                </div>
                <Zap className="w-4 h-4 text-[#A3A3A3]" />
              </button>
            ))}
          </div>
        )}
        {!state.loading && !state.error && state.results.length === 0 && state.origin && (
          <div className="text-sm text-[#A3A3A3] text-center py-6" data-testid="nearby-empty">
            No Rally Lab regions within {radius} miles yet. Widen the radius or check back soon.
          </div>
        )}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white" data-testid="back-to-dashboard">
      <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
    </Link>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="card-surface p-3 flex items-center gap-3" data-testid={testid}>
      <div className="text-[#CCFF00]">{icon}</div>
      <div className="min-w-0">
        <div className="text-lg font-bold leading-none">{value}</div>
        <div className="label-mono text-[#A3A3A3] mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}
